import { useEffect, useState } from "react";
import { Database, RotateCcw, Trash2 } from "lucide-react";
import { api, errorMessage } from "../lib/api";

export const AdminDemo = () => {
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/demo-accounts").then((r) => setData(r.data)).catch((e) => setError(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const purge = async () => {
    setError(""); setInfo(""); setBusy(true);
    try {
      const { data: res } = await api.post("/admin/demo-accounts/purge", { confirm });
      setInfo(`Suppression effectuée. Sauvegarde ${res.backup_id} vérifiée (${Object.entries(res.deleted).map(([k, v]) => `${k}: ${v}`).join(", ")}).`);
      setOpen(false); setConfirm(""); load();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  const restore = async (id) => {
    setError(""); setInfo(""); setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/backups/${id}/restore`);
      setInfo(`Restauration effectuée : ${Object.entries(res.restored).map(([k, v]) => `${k}: ${v}`).join(", ")}.`);
      load();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  const total = Object.values(data.counts).reduce((a, b) => a + b, 0);
  return (
    <section className="mt-10" data-testid="admin-demo">
      <h2 className="h-display text-3xl">Comptes <span className="text-rox-orange">démo</span></h2>
      <p className="mt-2 text-sm text-rox-off/75">Données de démonstration créées pendant le développement. Aucune suppression automatique : vérifie la liste, puis confirme. Une sauvegarde est écrite et vérifiée avant toute suppression. {data.real_users} compte{data.real_users > 1 ? "s" : ""} réel{data.real_users > 1 ? "s" : ""} préservé{data.real_users > 1 ? "s" : ""}.</p>
      {error && <p data-testid="admin-demo-error" className="mt-3 text-sm text-rox-orange">{error}</p>}
      {info && <p data-testid="admin-demo-info" className="mt-3 text-sm text-rox-off">{info}</p>}
      <div className="panel mt-4 p-4" data-testid="admin-demo-list">
        <p className="eyebrow">Comptes utilisateur démo · {data.users.length}</p>
        {data.users.length === 0 && <p className="mt-1 text-sm text-rox-muted">Aucun.</p>}
        {data.users.map((u) => <p key={u.id} className="mt-1 text-sm" data-testid={`demo-user-${u.id}`}>{u.name} · {u.email}</p>)}
        <p className="eyebrow mt-4">Faux profils · {data.profiles.length}</p>
        <p className="mt-1 text-sm text-rox-off/80">{data.profiles.map((p) => `${p.first_name} (${p.city})`).join(", ") || "Aucun."}</p>
        <p className="mt-4 text-xs text-rox-muted">Données liées : {data.counts.interests} intérêt(s) · {data.counts.matches} match(s) · {data.counts.plan_progress} progression(s) · {data.counts.reminders} rappel(s)</p>
      </div>
      {total > 0 && !open && (
        <button data-testid="admin-demo-purge-open" onClick={() => setOpen(true)} className="mt-3 flex w-full items-center justify-between rounded-[4px] border border-rox-orange/50 px-4 py-3 font-display text-sm uppercase tracking-wider text-rox-orange hover:bg-rox-orange/10">
          Supprimer les données démo ({total} éléments) <Trash2 size={16} />
        </button>
      )}
      {open && (
        <div className="panel mt-3 space-y-3 border-rox-orange/40 p-4" data-testid="admin-demo-confirm">
          <p className="text-sm">Tape <strong>SUPPRIMER</strong> pour confirmer. Les données seront sauvegardées puis supprimées ; une restauration restera possible ci-dessous.</p>
          <input data-testid="admin-demo-confirm-input" className="field" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="SUPPRIMER" />
          <div className="flex gap-2">
            <button data-testid="admin-demo-cancel" onClick={() => { setOpen(false); setConfirm(""); }} className="btn-ghost flex-1 py-3 text-sm">Annuler</button>
            <button data-testid="admin-demo-purge" onClick={purge} disabled={busy || confirm !== "SUPPRIMER"} className="btn-primary flex-1 py-3 text-sm">Confirmer la suppression</button>
          </div>
        </div>
      )}
      {data.backups.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="admin-backups">
          <p className="eyebrow">Sauvegardes</p>
          {data.backups.map((b) => (
            <div key={b.id} className="panel flex items-center gap-3 p-3" data-testid={`admin-backup-${b.id}`}>
              <Database size={16} className="shrink-0 text-rox-orange" />
              <div className="min-w-0 flex-1 text-xs">
                <p className="text-sm">{new Date(b.created_at).toLocaleString("fr-FR")}</p>
                <p className="text-rox-muted">{Object.entries(b.counts).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ")}{b.restored_at ? " · restaurée" : ""}</p>
              </div>
              <button data-testid={`admin-backup-restore-${b.id}`} onClick={() => restore(b.id)} disabled={busy} className="flex h-9 items-center gap-1.5 rounded-[3px] border border-rox-line px-3 text-[11px] font-semibold uppercase tracking-wider hover:border-rox-orange hover:text-rox-orange"><RotateCcw size={13} /> Restaurer</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
